import { AlertPayload } from "contexts/alert";
import { Config } from "env";
import React, { useEffect } from "react";
import {
    MdCalendarToday,
    MdOutlineLocalOffer,
    MdOutlineLocationOn,
} from "react-icons/md";
import { convertCity } from "../../commons/utils/convertLocation";
import { convertText } from "../../commons/utils/convertText";
import formatPrice, {
    convertStateToInitials,
    stringToSlug,
} from "../../commons/utils/Utils";
import { Button } from "../Button";
import { Card } from "../ui_kit/Card";
import styles from "./alert.module.scss";
import { ProductAvailable } from "./ProductAvailable";

interface AlertProps {
    id: string;
    data: AlertPayload;
    deleteAlert?: (id: string) => void;
    editAlert?: (data: any) => void;
}

export const Alert: React.FC<AlertProps> = ({
    id,
    data,
    deleteAlert,
    editAlert,
}) => {
    const {
        brand,
        model,
        yearMin: year_start,
        yearMax: year_end,
        priceMin: price_start,
        priceMax: price_end,
        location,
        category,
        shortDesc,
    } = data.product;

    const {
        offers: { total },
    } = data;

    useEffect(() => {
        getUrl(
            shortDesc,
            brand,
            model,
            location,
            year_start,
            year_end,
            price_start,
            price_end,
            category
        );
    }, [data]);

    function updatePlaceHolder(itemListSelect) {
        const hasItem = itemListSelect?.length;
        const city = itemListSelect[0]?.city;
        const state = itemListSelect[0]?.state;

        return !hasItem
            ? "Localização (opcional)"
            : hasItem === 1 && !city
            ? `${state}`
            : hasItem && state && city
            ? `${city}/${convertStateToInitials(state)}`
            : `${hasItem} localizações`;
    }
    function KeywordSplit(keyword: string) {
        const split = JSON.stringify(keyword.split(" "));
        return encodeURIComponent(split);
    }

    function getUrl(
        shortDesc,
        brand,
        model,
        location,
        year_start,
        year_end,
        price_start,
        price_end,
        category
    ) {
        const arrQuery = [];
        let formattedState;
        let formattedCity;

        if (location.length) {
            const arrState = [];
            const arrCity = [];
            location.forEach((loc, l) => {
                if (loc.city) {
                    formattedState = stringToSlug(loc.state);
                    formattedCity = `${stringToSlug(
                        loc.city
                    )}-${convertStateToInitials(loc.state).toLowerCase()}`;
                }
                if (!loc.city && loc.state) {
                    formattedState = stringToSlug(loc.state);
                }

                if (formattedCity && formattedCity.includes("sao-paulo-sp")) {
                    formattedCity = `[${stringToSlug(
                        loc.city
                    )}-${convertStateToInitials(
                        loc.state
                    ).toLowerCase()},embu-das-artes-sp]`;
                }

                arrState.push(formattedState);
                arrCity.push(convertCity(formattedCity));
            });

            if (location[0].state) {
                arrQuery.push(`product.location.state:${formattedState}`);
            }

            if (location[0].city) {
                arrQuery.push(`product.location.city:${formattedCity}`);
            }
        }

        if (brand && brand !== "undefined") {
            arrQuery.push(
                `product.brand.description:${convertText(brand.toLowerCase())}`
            );
        }

        if (model && model !== "undefined") {
            arrQuery.push(
                `product.model.description:${convertText(model.toLowerCase())}`
            );
        }

        if (year_start !== undefined && year_end !== undefined) {
            arrQuery.push(
                `product.template.aggs.identificacao.anofabricacao:range[${year_start}TO${year_end}]`
            );
        }

        arrQuery.push(`price:range[${price_start}TO${price_end}]`);

        let query = "";

        if (arrQuery.length) {
            query = arrQuery.join(";");
        }

        let url = "";

        if (category === "carros-motos") {
            url = `${Config.API_MARKETPLACE_URL}/categorias/carros-motos/carros?searchType=opened&pageNumber=1&pageSize=30&orderBy=price:desc&filter=${query}`;
        } else {
            url = `${
                Config.API_MARKETPLACE_URL
            }/ofertas/?searchType=opened&filter=product.shortDesc:LIKEAND${KeywordSplit(
                shortDesc
            )};${query}&pageNumber=1&pageSize=30&orderBy=price:desc`;
        }

        return url;
    }

    return (
        <Card id={`card ${id?.toString}`}>
            <div className={styles.card__front}>
                {/* Title */}
                <div className={styles.card__subtitle}>
                    {category === "outros" ? "Outros" : brand}
                </div>
                <div className={styles.card__title}>
                    {category === "outros" ? shortDesc : model}
                </div>

                {/* Location */}
                <div className={styles.card__location}>
                    <MdOutlineLocationOn className={styles.card__ico} />
                    {updatePlaceHolder(location)}
                </div>

                {/* Data Interval */}
                {year_start && year_end && category !== "outros" && (
                    <div className={styles.card__date_interval}>
                        <MdCalendarToday className={styles.card__ico} />
                        <div className={styles.card__txt}>
                            {year_start} a {year_end}
                        </div>
                    </div>
                )}

                {/* Prices Interval */}
                <div className={styles.card__price_interval}>
                    <MdOutlineLocalOffer className={styles.card__ico} />
                    {category !== "outros" ? (
                        <div className={styles.card__txt}>
                            {formatPrice.toBRL(price_start)} a
                            {formatPrice.toBRL(price_end)}
                        </div>
                    ) : (
                        <div className={styles.card__txt}>
                            {formatPrice.toDecimal(price_start)} a
                            {formatPrice.toDecimal(price_end)}
                        </div>
                    )}
                </div>

                {category === "outros" && (
                    <div className="divisor" style={{ height: "25px" }}></div>
                )}

                {/* Buttons */}
                <div className={styles.card__buttons}>
                    <Button
                        label="Editar"
                        template={"gray"}
                        icon="MdOutlineModeEditOutline"
                        className={styles.card__btn}
                        onClick={() =>
                            editAlert({
                                id,
                                brand,
                                model,
                                location,
                                year_start,
                                year_end,
                                price_start,
                                price_end,
                                category,
                                shortDesc,
                            })
                        }
                    />

                    <Button
                        label="Excluir"
                        template={"gray"}
                        icon="MdDeleteOutline"
                        className={styles.card__btn}
                        onClick={() => deleteAlert(id)}
                    />
                </div>

                <ProductAvailable
                    onClickUrl={getUrl(
                        shortDesc,
                        brand,
                        model,
                        location,
                        year_start,
                        year_end,
                        price_start,
                        price_end,
                        category
                    )}
                    qtd={total.toString()}
                />
            </div>

            <div className={styles.card__back}>Lista de regiões</div>
        </Card>
    );
};
